/**
 * APC Tabs — store-specific override of tabs.js
 *
 * Moves live DOM nodes instead of copying innerHTML, so app blocks
 * and other JS-initialised components survive the accordion → tabs transform.
 *
 * Loads INSTEAD of tabs.js (replace the script src in the accordion block).
 */

class TabsInitializer {
  /** @type {WeakSet<HTMLElement>} */
  static #initialized = new WeakSet();

  /**
   * Initialize tabs on all accordion blocks with data-display-mode="tabs"
   */
  static initAll() {
    const containers = document.querySelectorAll('[data-display-mode="tabs"]:not(.tabs-initialized)');
    for (const container of containers) {
      if (container instanceof HTMLElement && !this.#initialized.has(container)) {
        new TabsController(container);
        this.#initialized.add(container);
      }
    }
  }
}

class TabsController {
  /** @type {HTMLElement} */
  #container;

  /** @type {HTMLElement|null} */
  #tablist;

  /** @type {HTMLElement|null} */
  #panelsContainer;

  /** @type {HTMLElement|null} */
  #accordion;

  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement[]} */
  #accordionContents = [];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {MediaQueryList} */
  #mobileMediaQuery = window.matchMedia('(width < 64rem)');

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {'tabs'|'accordion'} */
  get #mobileDisplay() {
    return /** @type {'tabs'|'accordion'} */ (this.#container.dataset.mobileDisplay || 'accordion');
  }

  /** @type {boolean} */
  get #isMobile() {
    return this.#mobileMediaQuery.matches;
  }

  /** @type {boolean} */
  get #useAccordionOnMobile() {
    return this.#mobileDisplay === 'accordion';
  }

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.#container = container;
    this.#tablist = container.querySelector('.tabs-tablist');
    this.#panelsContainer = container.querySelector('.tabs-panels');
    this.#accordion = container.querySelector('details-accordion');

    if (!this.#tablist || !this.#panelsContainer || !this.#accordion) {
      console.warn('TabsController: Missing required elements');
      return;
    }

    this.#transformAccordionToTabs();
    this.#setupAria();
    this.#activateTab(this.#activeIndex, false);
    this.#applyResponsiveMode();
    this.#container.classList.add('tabs-initialized');

    // Set up responsive behavior
    if (this.#useAccordionOnMobile) {
      this.#mobileMediaQuery.addEventListener('change', this.#handleMediaQueryChange, {
        signal: this.#abortController.signal,
      });
    }
  }

  /**
   * Transforms accordion rows into tab panels and creates tab buttons.
   */
  #transformAccordionToTabs() {
    if (!this.#tablist || !this.#panelsContainer || !this.#accordion) return;

    // Find all accordion rows (details-custom elements)
    const accordionRows = this.#accordion.querySelectorAll('details-custom');
    if (!accordionRows.length) return;

    for (let i = 0; i < accordionRows.length; i++) {
      const row = accordionRows[i];
      const labelText = (row.dataset.tabLabel || '').trim();
      // The leading icon/image (preset SVG or custom uploaded image) rendered by
      // the accordion row. Cloning it preserves its markup and inline size var.
      const iconEl = row.querySelector('.details__summary__heading-icon');

      // Create tab button
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tabs-tab';
      tab.setAttribute('role', 'tab');

      // Carry the icon into the tab so selecting an icon shows in the tab bar.
      if (iconEl) {
        const iconClone = /** @type {HTMLElement} */ (iconEl.cloneNode(true));
        iconClone.classList.add('tabs-tab__icon');
        tab.appendChild(iconClone);
        tab.classList.add('tabs-tab--has-icon');
      }

      // Render the heading text when present. When it's empty, let the icon
      // stand in for the label instead of falling back to "Tab N". The generic
      // fallback only applies when there is neither a heading nor an icon.
      if (labelText) {
        const labelSpan = document.createElement('span');
        labelSpan.className = 'tabs-tab__label';
        labelSpan.textContent = labelText;
        tab.appendChild(labelSpan);
      } else if (!iconEl) {
        tab.textContent = `Tab ${i + 1}`;
      }

      // Always expose an accessible name (the cloned icon is aria-hidden).
      tab.setAttribute('aria-label', labelText || `Tab ${i + 1}`);

      tab.addEventListener('click', () => this.#activateTab(i), { signal: this.#abortController.signal });
      this.#tablist.appendChild(tab);
      this.#tabs.push(tab);

      // Create panel from accordion row content
      const panel = document.createElement('div');
      panel.className = 'tabs-panel';
      panel.setAttribute('role', 'tabpanel');

      // Move (not copy) live DOM nodes to preserve app blocks and event listeners
      const contentWrapper = row.querySelector('.details__content');
      if (contentWrapper) {
        while (contentWrapper.firstChild) {
          panel.appendChild(contentWrapper.firstChild);
        }
        this.#accordionContents.push(contentWrapper);
      }

      this.#panelsContainer.appendChild(panel);
      this.#panels.push(panel);
    }

    // Bind keyboard navigation
    this.#tablist.addEventListener('keydown', (e) => this.#handleKeydown(e), { signal: this.#abortController.signal });
  }

  /**
   * Sets up ARIA roles and attributes.
   */
  #setupAria() {
    if (!this.#tablist) return;

    this.#tablist.setAttribute('role', 'tablist');

    for (let i = 0; i < this.#tabs.length; i++) {
      const tab = this.#tabs[i];
      const panel = this.#panels[i];
      if (!tab || !panel) continue;

      const id = this.#generateId(i);
      tab.id = `tab-${id}`;
      panel.id = `panel-${id}`;
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', tab.id);
    }
  }

  /**
   * Handles media query change events.
   */
  #handleMediaQueryChange = () => {
    this.#applyResponsiveMode();
  };

  /**
   * Applies the correct display mode based on viewport.
   */
  #applyResponsiveMode() {
    if (!this.#tablist || !this.#panelsContainer || !this.#accordion) return;

    if (this.#isMobile && this.#useAccordionOnMobile) {
      // Move content back to accordion
      this.#moveContentToAccordion();
      this.#container.classList.add('mobile-accordion-mode');
      this.#tablist.hidden = true;
      this.#panelsContainer.hidden = true;
      this.#accordion.hidden = false;
    } else {
      // Move content to tab panels
      this.#moveContentToPanels();
      this.#container.classList.remove('mobile-accordion-mode');
      this.#tablist.hidden = false;
      this.#panelsContainer.hidden = false;
      this.#accordion.hidden = true;
      // Re-activate current tab
      this.#activateTab(this.#activeIndex, false);
    }
  }

  /**
   * Moves content from tab panels back to accordion content wrappers.
   */
  #moveContentToAccordion() {
    for (let i = 0; i < this.#panels.length; i++) {
      const panel = this.#panels[i];
      const target = this.#accordionContents[i];
      if (!panel || !target || !panel.firstChild) continue;
      while (panel.firstChild) {
        target.appendChild(panel.firstChild);
      }
    }
  }

  /**
   * Moves content from accordion content wrappers to tab panels.
   */
  #moveContentToPanels() {
    for (let i = 0; i < this.#panels.length; i++) {
      const panel = this.#panels[i];
      const source = this.#accordionContents[i];
      if (!panel || !source || !source.firstChild) continue;
      while (source.firstChild) {
        panel.appendChild(source.firstChild);
      }
    }
  }

  /**
   * Activates the tab at the given index.
   *
   * @param {number} index
   * @param {boolean} [animate=true]
   */
  #activateTab(index, animate = true) {
    if (index < 0 || index >= this.#tabs.length) return;

    // Update tab states
    for (let i = 0; i < this.#tabs.length; i++) {
      const tab = this.#tabs[i];
      const panel = this.#panels[i];
      const isActive = i === index;

      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('tabs-tab--active', isActive);
      panel.hidden = !isActive;
    }

    this.#activeIndex = index;

    // Scroll tab into view
    if (animate) {
      this.#tabs[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  /**
   * Handles keyboard navigation (WAI-ARIA Tabs Pattern).
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const i = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (i === -1) return;

    // Handle Space/Enter to activate
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#activateTab(i);
      return;
    }

    // Navigation map
    /** @type {Record<string, number>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: this.#tabs.length - 1 - i,
    };

    const offset = navMap[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      const nextIndex = (i + offset + this.#tabs.length) % this.#tabs.length;
      this.#tabs[nextIndex]?.focus();
      this.#activateTab(nextIndex);
    }
  }

  /**
   * Generates a unique ID for tab/panel pairs.
   *
   * @param {number} index
   * @returns {string}
   */
  #generateId(index) {
    const baseId =
      this.#container.id || this.#container.dataset.sectionId || `tabs-${Math.random().toString(36).slice(2, 8)}`;
    return `${baseId}-${index}`;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => TabsInitializer.initAll());
} else {
  TabsInitializer.initAll();
}

// Re-initialize when Shopify editor updates
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => TabsInitializer.initAll());
  document.addEventListener('shopify:block:select', () => TabsInitializer.initAll());
}

export { TabsInitializer, TabsController };
